import { createAsyncThunk } from '@reduxjs/toolkit'

import Api from '@services'
import {
  clearAuthSession,
  getProfileFromResponse,
  isClubPortalProfile,
  mapProfileToAuthUser,
  persistAuthTokens
} from '@utils/auth-session'
import { IChangePassword, ILoginView, IResetPassword, ISetPasswordPayload } from '@store/features/auth/auth.types'

const clubPortalReject = {
  response: { data: { errors: { error: 'CLUB_PORTAL_ONLY' } } }
}

const invalidCredentialsReject = {
  response: { data: { errors: { error: 'INVALID_CREDENTIALS' } } }
}

export const loginThunk = createAsyncThunk(
  'auth/login',
  async (payload: ILoginView, { rejectWithValue }) => {
    const { rememberMe, ...payloadData } = payload

    try {
      const tokenResponse = await Api.auth.login(payloadData)
      const tokenData = tokenResponse.data?.data

      if (!tokenData?.accessToken) {
        clearAuthSession()
        return rejectWithValue(invalidCredentialsReject)
      }

      const refreshToken = tokenData.refreshToken ?? tokenData.accessToken
      const profileResponse = await Api.auth.getProfileWithToken(tokenData.accessToken)
      const profile = getProfileFromResponse(profileResponse)

      if (!isClubPortalProfile(profile)) {
        clearAuthSession()
        return rejectWithValue(clubPortalReject)
      }

      if (!mapProfileToAuthUser(profile!)) {
        clearAuthSession()
        return rejectWithValue(invalidCredentialsReject)
      }

      persistAuthTokens(tokenData.accessToken, refreshToken, rememberMe)

      return {
        accessToken: tokenData.accessToken,
        refreshToken,
        userData: profile!
      }
    } catch (error) {
      clearAuthSession()
      return rejectWithValue(error)
    }
  }
)

export const initializeSession = createAsyncThunk(
  'auth/initializeSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.auth.getProfile()
      const profile = getProfileFromResponse(response)

      if (!isClubPortalProfile(profile) || !mapProfileToAuthUser(profile!)) {
        clearAuthSession()
        return rejectWithValue(clubPortalReject)
      }

      return { userData: profile! }
    } catch (error) {
      clearAuthSession()
      return rejectWithValue(error)
    }
  }
)

/** @deprecated Use initializeSession */
export const profileThunk = initializeSession

export const logoutThunk = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await Api.auth.logout()
    clearAuthSession()
  } catch (error) {
    clearAuthSession()
    return rejectWithValue(error)
  }
})

export const changePasswordThunk = createAsyncThunk(
  'auth/password-change',
  async (payload: IChangePassword, { rejectWithValue }) => {
    try {
      await Api.auth.changePassword(payload)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const resetPasswordThunk = createAsyncThunk(
  'auth/password-reset',
  async (payload: IResetPassword, { rejectWithValue }) => {
    try {
      await Api.auth.resetPassword(payload)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const setPasswordThunk = createAsyncThunk(
  'auth/password-set',
  async (payload: ISetPasswordPayload, { rejectWithValue }) => {
    try {
      await Api.auth.setPassword(payload)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
